import React from 'react';

import { type Selector, Combobox } from '@lumx/react';

import { isComponentType } from '@lumapps/utils/types/isComponentType';

import { getWithSelector } from '../core/utils/selectors';

const SCOPE = 'lumx-combobox-option';

/**
 * Wraps the consumer's `renderOption` to automatically inject `data-id`.
 * `renderOption` must return a `Combobox.Option` as its root element — any other type renders nothing.
 */
export function useComboboxRenderOptionWithDataId<O>(
    renderOption: ((option: O, index: number) => React.ReactNode) | undefined,
    getOptionId: Selector<O> | undefined,
    getOptionName: Selector<O, string | undefined | null> | undefined,
) {
    return React.useCallback(
        (option: O, index: number): React.ReactNode => {
            // Compute the option id using the same logic as the DS core
            const optionId = getWithSelector((getOptionId || getOptionName) as Selector<O>, option);
            const dataId = optionId != null ? `${SCOPE}-option-${optionId}`.toLowerCase() : undefined;

            // Call the consumer's renderOption if provided
            const customNode = renderOption?.(option, index);
            const props = isComponentType(Combobox.Option, customNode) ? customNode.props : undefined;
            if (renderOption && !props) {
                return null;
            }

            return <Combobox.Option data-id={dataId} {...props} />;
        },
        [renderOption, getOptionId, getOptionName],
    );
}
